'use client';

import Link from 'next/link';
import { MenuItem } from '@/types/api';
import { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { productAPI } from '@/lib/api';
import { usePathname } from 'next/navigation';

interface NavigationProps {
  menu?: {
    items: MenuItem[];
  };
  sticky?: boolean;
}

export default function Navigation({ menu, sticky = false }: NavigationProps) {
  const [activeMenu, setActiveMenu] = useState<string | null>(null);
  const [isClient, setIsClient] = useState(false);
  const pathname = usePathname();

  useEffect(() => {
    setIsClient(true);
  }, []);

  const { data: categoriesData } = useQuery({
    queryKey: ['product-categories'],
    queryFn: () => productAPI.getCategories(),
    staleTime: 5 * 60 * 1000,
  });

  const productCategories = categoriesData?.data || [];

  const buildCategoryMenuItems = (categories: any[]): MenuItem[] => {
    if (!categories || categories.length === 0) return [];

    const categoryMap = new Map<string, MenuItem & { children?: MenuItem[] }>();
    const rootItems: MenuItem[] = [];

    categories.forEach(cat => {
      const menuItem: MenuItem & { children?: MenuItem[] } = {
        id: `category-${cat.id}`,
        title: cat.name,
        url: `/products?category=${cat.slug}`,
        target: '_self',
        status: cat.status || 'active',
        sortOrder: cat.sortOrder || 0,
        children: [],
      };
      categoryMap.set(cat.id, menuItem);
    });

    categories.forEach(cat => {
      const menuItem = categoryMap.get(cat.id)!;
      if (cat.parentId && categoryMap.has(cat.parentId)) {
        const parent = categoryMap.get(cat.parentId)!;
        if (!parent.children) parent.children = [];
        parent.children.push(menuItem);
      } else {
        rootItems.push(menuItem);
      }
    });

    const sortItems = (items: MenuItem[]): MenuItem[] => {
      return items
        .sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0))
        .map(item => ({
          ...item,
          children: item.children?.length ? sortItems(item.children) : undefined,
        }));
    };

    return sortItems(rootItems);
  };

  const mergeProductCategoriesToMenu = (menuItems: MenuItem[]): MenuItem[] => {
    if (!menuItems || menuItems.length === 0) return [];
    
    return menuItems.map(item => {
      if (item.title === 'Products' && item.url === '/products') {
        const categoryItems = buildCategoryMenuItems(productCategories);
        return {
          ...item,
          children: categoryItems,
        };
      }
      return item;
    });
  };

  const getMenuItems = (): MenuItem[] => {
    if (!menu || !menu.items) return [];
    
    const itemsWithCategories = mergeProductCategoriesToMenu(menu.items);
    
    const filteredItems = itemsWithCategories.filter(item => {
      return item.status === 'active' && 
             item.title && 
             item.url && 
             !item.url.startsWith('/admin');
    });
    
    return filteredItems;
  };

  const getMenuItemTypeClasses = (url: string): string => {
    if (!url || url === '#' || url.startsWith('/login')) {
      return 'menu-item-type-custom menu-item-object-custom';
    }
    
    if (url === '/' || url === '/home') {
      return 'menu-item-type-post_type menu-item-object-page menu-item-home';
    }
    
    if (url.includes('/products') || url.includes('/product')) {
      return 'menu-item-type-taxonomy menu-item-object-products';
    }
    
    if (url.includes('/news') || url.includes('/category')) {
      return 'menu-item-type-taxonomy menu-item-object-category';
    }
    
    if (url.startsWith('/')) {
      const excludedPages = ['about-us', 'contact-us', 'download-center', 'video', 'case-center', 'faqs'];
      const isExcluded = excludedPages.some(page => url === '/' + page);
      if (!isExcluded) {
        return 'menu-item-type-custom menu-item-object-custom';
      }
    }
    
    return 'menu-item-type-post_type menu-item-object-page';
  };

  const isCurrentPage = (url: string): boolean => {
    if (!url || url === '#') return false;
    const normalizedUrl = url === '/home' ? '/' : url;
    const normalizedPathname = (isClient && pathname) ? pathname : '/';
    return normalizedUrl === normalizedPathname || normalizedPathname.startsWith(normalizedUrl + '/');
  };

  const renderMenuItem = (item: MenuItem, level: number = 0) => {
    const hasChildren = item.children && item.children.length > 0;
    const isActive = activeMenu === item.id;
    const normalizedUrl = item.url === '/home' ? '/' : (item.url || '#');
    const isCurrent = isCurrentPage(item.url || '');
    const menuTypeClasses = getMenuItemTypeClasses(item.url || '');
    
    const menuItemClasses = [
      'menu-item',
      menuTypeClasses,
      hasChildren ? 'menu-item-has-children' : '',
      isCurrent ? 'current-menu-item page_item current_page_item' : '',
      `menu-item-${item.id}`
    ].filter(Boolean).join(' ');

    return (
      <li
        key={item.id}
        id={`menu-item-${item.id}`}
        className={`${menuItemClasses} ${hasChildren ? 'menu-item-has-children' : ''}`}
      >
        <Link
          href={normalizedUrl}
          className="menu-link"
          style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}
          onClick={() => {
            if (level === 0) {
              setActiveMenu(null);
            }
          }}
        >
          <span className="menu-text">
            {item.title}
          </span>
          {hasChildren && (
            <span className="ast-header-navigation-arrow" aria-label="Menu Toggle" style={{ marginLeft: '8px', flexShrink: 0 }}>
              <span className="ast-icon icon-arrow">
                <svg className="ast-arrow-svg" xmlns="http://www.w3.org/2000/svg" width="26" height="16" viewBox="57 35.171 26 16.043">
                  <path d="M57.5,38.193l12.5,12.5l12.5-12.5l-2.5-2.5l-10,10l-10-10L57.5,38.193z" />
                </svg>
              </span>
            </span>
          )}
        </Link>
        {hasChildren && (
          <ul
            className="sub-menu"
            style={{
              position: 'absolute',
              left: level === 0 ? '0' : '100%',
              top: level === 0 ? '100%' : '0',
              zIndex: level === 0 ? 100001 : 100002,
              backgroundColor: '#ffffff',
              boxShadow: '0 4px 10px -2px rgba(0, 0, 0, 0.1)',
              border: '1px solid #e0e0e0',
              margin: 0,
              padding: 0,
              listStyle: 'none',
              minWidth: '200px',
            }}
          >
            {item.children?.map((child) => renderMenuItem(child, level + 1))}
          </ul>
        )}
      </li>
    );
  };

  const activeItems = getMenuItems();

  useEffect(() => {
    console.log('Navigation Debug:', {
      menu,
      hasMenu: !!menu,
      hasItems: !!menu?.items,
      itemsIsArray: Array.isArray(menu?.items),
      itemsCount: menu?.items?.length || 0,
      activeItems,
      activeItemsCount: activeItems.length,
    });
  }, [menu, activeItems]);

  if (!activeItems || activeItems.length === 0) {
    return (
      <nav className="flex items-center space-x-1">
        <Link href="/" className="px-4 py-2 text-text-dark hover:text-primary font-medium transition-colors">
          首页
        </Link>
        <Link href="/products" className="px-4 py-2 text-text-dark hover:text-primary font-medium transition-colors">
          产品
        </Link>
        <Link href="/news" className="px-4 py-2 text-text-dark hover:text-primary font-medium transition-colors">
          新闻
        </Link>
        <Link href="/about-us" className="px-4 py-2 text-text-dark hover:text-primary font-medium transition-colors">
          关于我们
        </Link>
        <Link href="/contact-us" className="px-4 py-2 text-text-dark hover:text-primary font-medium transition-colors">
          联系我们
        </Link>
      </nav>
    );
  }

  return (
    <nav 
      className="site-navigation ast-flex-grow-1 navigation-accessibility site-header-focus-item ast-desktop-menu"
      id={sticky ? "primary-site-navigation-desktop-sticky" : "primary-site-navigation-desktop"} 
      aria-label="Site Navigation: Main Menu" 
      itemScope
    >
      <div className="main-navigation ast-inline-flex">
        <ul 
          id={sticky ? "ast-hf-menu-1-sticky" : "ast-hf-menu-1"} 
          className="main-header-menu ast-menu-shadow ast-nav-menu ast-flex submenu-with-border stack-on-mobile ast-mega-menu-enabled"
        >
          {activeItems.map((item) => renderMenuItem(item, 0))}
        </ul>
      </div>
    </nav>
  );
}
